import React, { useState, useMemo } from 'react';

export default function MonteCarlo() {
  const [winRate, setWinRate] = useState(50);
  const [rrRatio, setRrRatio] = useState(2.5);
  const [riskPct, setRiskPct] = useState(1);
  const [trades, setTrades] = useState(100);
  const [simulations, setSimulations] = useState(1000);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);

  function runSimulation() {
    setRunning(true);
    setTimeout(() => {
      const allCurves = [];
      const finalReturns = [];
      const maxDrawdowns = [];
      let ruinCount = 0;
      const ruinThreshold = -50; // 50% drawdown = ruin

      for (let s = 0; s < simulations; s++) {
        let equity = 0;
        let peak = 0;
        let maxDD = 0;
        const curve = [0];
        let ruined = false;

        for (let i = 0; i < trades; i++) {
          const win = Math.random() * 100 < winRate;
          const result = win ? rrRatio : -1;
          equity += result * riskPct;
          if (equity > peak) peak = equity;
          const dd = peak - equity;
          if (dd > maxDD) maxDD = dd;
          curve.push(equity);
          if (equity <= ruinThreshold && !ruined) { ruined = true; }
        }

        if (ruined) ruinCount++;
        allCurves.push(curve);
        finalReturns.push(equity);
        maxDrawdowns.push(maxDD);
      }

      finalReturns.sort((a, b) => a - b);
      maxDrawdowns.sort((a, b) => a - b);

      const median = finalReturns[Math.floor(simulations / 2)];
      const p5 = finalReturns[Math.floor(simulations * 0.05)];
      const p95 = finalReturns[Math.floor(simulations * 0.95)];
      const avgFinal = finalReturns.reduce((a,b) => a+b, 0) / simulations;
      const medianDD = maxDrawdowns[Math.floor(simulations / 2)];
      const p95DD = maxDrawdowns[Math.floor(simulations * 0.95)];
      const profitableSims = finalReturns.filter(r => r > 0).length;
      const profitablePct = (profitableSims / simulations) * 100;
      const ruinPct = (ruinCount / simulations) * 100;

      // Pick 30 random curves for visualization (to keep SVG light)
      const sampleCurves = [];
      const sampleSize = Math.min(50, simulations);
      const step = Math.floor(simulations / sampleSize);
      for (let i = 0; i < sampleSize; i++) {
        sampleCurves.push(allCurves[i * step]);
      }

      setResults({
        median, p5, p95, avgFinal, medianDD, p95DD,
        profitablePct, ruinPct, sampleCurves,
        finalReturns
      });
      setRunning(false);
    }, 50);
  }

  const expectancy = (winRate / 100) * rrRatio - (1 - winRate / 100);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Monte Carlo <em>Simulator</em></h1>
        <p className="page-subtitle">Run {simulations.toLocaleString()} simulated trade sequences to see your real risk profile</p>
      </div>

      <div className="card mb-24">
        <div className="card-title">SIMULATION PARAMETERS</div>
        <div className="calc-inputs" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20}}>
          {[
            {label:'Win Rate (%)', val:winRate, set:setWinRate, min:10, max:90, step:1, fmt:v=>v+'%'},
            {label:'Risk:Reward Ratio', val:rrRatio, set:setRrRatio, min:0.5, max:10, step:0.1, fmt:v=>'1:'+v.toFixed(1)},
            {label:'Risk per Trade (%)', val:riskPct, set:setRiskPct, min:0.1, max:5, step:0.1, fmt:v=>v.toFixed(1)+'%'},
            {label:'Number of Trades', val:trades, set:setTrades, min:10, max:1000, step:10, fmt:v=>v},
          ].map(f => (
            <div key={f.label} className="calc-field">
              <label>{f.label}</label>
              <div className="range-row">
                <input type="range" min={f.min} max={f.max} step={f.step} value={f.val}
                  onChange={e => f.set(parseFloat(e.target.value))} style={{flex:1}} />
                <span className="range-val">{f.fmt(f.val)}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{marginTop:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:220}}>
            <div style={{fontSize:11,color:'var(--text-3)',fontFamily:'DM Mono,monospace',letterSpacing:1}}>EXPECTED VALUE PER TRADE</div>
            <div style={{fontFamily:'DM Mono,monospace',fontSize:18,color: expectancy > 0 ? 'var(--green)' : 'var(--red)', marginTop:4}}>
              {expectancy > 0 ? '+' : ''}{expectancy.toFixed(3)}R · {(expectancy * riskPct).toFixed(3)}% of account
            </div>
          </div>
          <button className="btn-gold" onClick={runSimulation} disabled={running}>
            {running ? 'Simulating...' : `Run ${simulations.toLocaleString()} Simulations`}
          </button>
        </div>
      </div>

      {results && (
        <>
          <div className="kpi-grid">
            <div className="kpi-cell">
              <div className="kpi-label">PROFITABLE SCENARIOS</div>
              <div className="kpi-value" style={{color: results.profitablePct >= 50 ? 'var(--green)' : 'var(--red)'}}>
                {results.profitablePct.toFixed(1)}%
              </div>
              <div className="kpi-note">of {simulations.toLocaleString()} sims</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">MEDIAN RETURN</div>
              <div className="kpi-value" style={{color: results.median >= 0 ? 'var(--green)' : 'var(--red)'}}>
                {results.median > 0 ? '+' : ''}{results.median.toFixed(2)}%
              </div>
              <div className="kpi-note">50th percentile</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">WORST CASE (P5)</div>
              <div className="kpi-value" style={{color:'var(--red)'}}>
                {results.p5.toFixed(2)}%
              </div>
              <div className="kpi-note">5th percentile</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">BEST CASE (P95)</div>
              <div className="kpi-value" style={{color:'var(--green)'}}>
                +{results.p95.toFixed(2)}%
              </div>
              <div className="kpi-note">95th percentile</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">MEDIAN MAX DRAWDOWN</div>
              <div className="kpi-value" style={{color:'var(--amber)'}}>
                -{results.medianDD.toFixed(2)}%
              </div>
              <div className="kpi-note">typical worst dip</div>
            </div>
            <div className="kpi-cell">
              <div className="kpi-label">RISK OF RUIN (50%)</div>
              <div className="kpi-value" style={{color: results.ruinPct < 5 ? 'var(--green)' : results.ruinPct < 20 ? 'var(--amber)' : 'var(--red)'}}>
                {results.ruinPct.toFixed(1)}%
              </div>
              <div className="kpi-note">chance of -50%</div>
            </div>
          </div>

          <div className="card mb-24">
            <div className="card-title">EQUITY CURVES — {results.sampleCurves.length} SAMPLE SIMULATIONS</div>
            <MonteCarloChart curves={results.sampleCurves} />
          </div>

          <div className="card">
            <div className="card-title">INTERPRETATION</div>
            <div style={{fontSize:14, color:'var(--text-2)', lineHeight:1.8}}>
              {results.profitablePct < 50 && (
                <p style={{color:'var(--red)', marginBottom:12}}>
                  ⚠️ Negative expectancy — fewer than half your simulations end profitable. These parameters are losing money in the long run.
                </p>
              )}
              {results.ruinPct > 10 && (
                <p style={{color:'var(--amber)', marginBottom:12}}>
                  ⚠️ Risk of ruin is too high at <strong>{results.ruinPct.toFixed(1)}%</strong>. Lower your risk per trade.
                </p>
              )}
              {results.ruinPct < 1 && expectancy > 0 && (
                <p style={{color:'var(--green)', marginBottom:12}}>
                  ✓ Excellent risk profile. Less than 1% chance of ruin with positive expectancy.
                </p>
              )}
              <p>
                Over <strong>{trades} trades</strong>, the median outcome is <strong style={{color:'var(--gold-2)'}}>{results.median > 0 ? '+' : ''}{results.median.toFixed(2)}%</strong> on your account.
                Half of all simulations finished between <strong>{results.p5.toFixed(2)}%</strong> (bad luck) and <strong>+{results.p95.toFixed(2)}%</strong> (good luck).
                Even with positive expectancy, expect to experience a drawdown of around <strong style={{color:'var(--amber)'}}>{results.medianDD.toFixed(1)}%</strong> at some point.
              </p>
            </div>
          </div>
        </>
      )}

      {!results && !running && (
        <div className="card" style={{textAlign:'center', padding:48}}>
          <div style={{fontSize:40,marginBottom:12}}>🎲</div>
          <p style={{color:'var(--text-3)', maxWidth:480, margin:'0 auto', lineHeight:1.7}}>
            Set your parameters above and click <strong style={{color:'var(--gold-2)'}}>Run Simulations</strong> to see {simulations.toLocaleString()} possible futures
            based on your win rate, R:R ratio, and risk size. This reveals what trading really looks like — including the unavoidable variance.
          </p>
        </div>
      )}
    </div>
  );
}

function MonteCarloChart({ curves }) {
  if (!curves || curves.length === 0) return null;

  const width = 800;
  const height = 280;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const maxLen = Math.max(...curves.map(c => c.length));
  let yMin = 0, yMax = 0;
  curves.forEach(c => {
    c.forEach(v => {
      if (v < yMin) yMin = v;
      if (v > yMax) yMax = v;
    });
  });
  const yRange = (yMax - yMin) || 1;

  const xScale = (i) => padding.left + (i / (maxLen - 1)) * innerW;
  const yScale = (v) => padding.top + innerH - ((v - yMin) / yRange) * innerH;

  // Sort to find median curve
  const finalVals = curves.map((c, i) => ({ idx: i, final: c[c.length-1] }));
  finalVals.sort((a, b) => a.final - b.final);
  const medianIdx = finalVals[Math.floor(finalVals.length / 2)].idx;

  const yTicks = [yMin, yMin + yRange*0.25, yMin + yRange*0.5, yMin + yRange*0.75, yMax];

  return (
    <div style={{width:'100%', overflowX:'auto'}}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{width:'100%', minWidth:'500px', display:'block'}}>
        {/* Y grid */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line x1={padding.left} y1={yScale(tick)} x2={width - padding.right} y2={yScale(tick)}
              stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
            <text x={padding.left - 8} y={yScale(tick) + 4} textAnchor="end" fontSize="10" fill="#5a5248" fontFamily="DM Mono, monospace">
              {tick.toFixed(1)}%
            </text>
          </g>
        ))}

        {/* Zero line */}
        {yMin < 0 && (
          <line x1={padding.left} y1={yScale(0)} x2={width - padding.right} y2={yScale(0)}
            stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="3,3" />
        )}

        {/* All curves */}
        {curves.map((curve, ci) => {
          const final = curve[curve.length - 1];
          const isMedian = ci === medianIdx;
          const color = final > 0 ? 'rgba(62,207,142,0.2)' : 'rgba(224,82,82,0.2)';
          const path = curve.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(v)}`).join(' ');
          return (
            <path key={ci} d={path}
              fill="none"
              stroke={isMedian ? '#C9973A' : color}
              strokeWidth={isMedian ? 2 : 0.8} />
          );
        })}

        <text x={padding.left} y={height - 8} fontSize="10" fill="#5a5248" fontFamily="DM Mono, monospace">Trade 1</text>
        <text x={width - padding.right} y={height - 8} fontSize="10" fill="#5a5248" textAnchor="end" fontFamily="DM Mono, monospace">Trade {maxLen-1}</text>

        <text x={width - padding.right - 8} y={padding.top + 12} textAnchor="end" fontSize="10" fill="#C9973A" fontFamily="DM Mono, monospace">
          ━ median path
        </text>
      </svg>
    </div>
  );
}
